// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the small brand pieces Agents shows: the T3 Code mark (a text mark, no bundled image),
// the provider logo on a thread, tool chips with versions and the machine glyph (laptop or server).

import styles from '@app/features/konvo_agents/components/AgentBrand.module.css';
import {type AgentAccount, validAccountColor} from '@app/features/konvo_agents/state/AgentAccounts';
import {machineIcon, providerKind} from '@app/features/konvo_agents/state/AgentsModel';
import {ClaudeLogo} from '@app/features/ui/components/icons/ClaudeLogo';
import {OpenAILogo} from '@app/features/ui/components/icons/OpenAILogo';
import type {AgentMachineView, AgentToolInfo} from '@fluxer/schema/src/domains/konvo/Agents';
import {IconDeviceLaptop, IconServer} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type {CSSProperties} from 'react';

export function accountColorStyle(color: string | undefined): CSSProperties | undefined {
	const accent = validAccountColor(color);
	return accent
		? ({'--account-tone': accent, '--provider-accent': accent, '--usage-fill': accent} as CSSProperties)
		: undefined;
}

/** T3 Code's mark: a dark rounded tile with "T3". */
export function T3Mark({size = 16}: {size?: number}) {
	return (
		<svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className={styles.t3}>
			<rect width="16" height="16" rx="4" fill="currentColor" />
			<text
				x="8"
				y="11.4"
				textAnchor="middle"
				fontSize="8.6"
				fontWeight="800"
				fontFamily="ui-sans-serif, system-ui, sans-serif"
				fill="var(--t3-mark-ink, #fff)"
			>
				T3
			</text>
		</svg>
	);
}

/**
 * The logo of whatever runs the thread (Claude, Codex), else T3 Code's mark. With `account` (two
 * logins of one agent) the login's first letter sits on its corner; `out` adds a red dot for a
 * used-up plan.
 */
export function ProviderLogo({
	provider,
	size = 18,
	account,
	out = false,
}: {
	provider: string | undefined;
	size?: number;
	account?: AgentAccount | null;
	out?: boolean;
}) {
	const logo = providerLogo(provider, size);
	if (!account && !out) return logo;
	return (
		<span
			className={styles.badged}
			style={accountColorStyle(account?.color)}
			data-tone={account?.tone}
			title={account?.label}
		>
			{logo}
			{account && (
				<span className={clsx(styles.letter, size < 15 && styles.letterSmall)} aria-hidden="true">
					{account.letter}
				</span>
			)}
			{out && <span className={styles.outDot} aria-hidden="true" />}
		</span>
	);
}

function providerLogo(provider: string | undefined, size: number) {
	const kind = providerKind(provider);
	if (kind === 'claude') return <ClaudeLogo size={size} />;
	if (kind === 'codex')
		return (
			<span className={styles.openai}>
				<OpenAILogo size={size} />
			</span>
		);
	return <T3Mark size={size} />;
}

/** The login's name in a tinted pill ("Personal"), beside a model or on a chat row. */
export function AccountPill({account, className}: {account: AgentAccount; className?: string}) {
	return (
		<span
			className={clsx(styles.accountPill, className)}
			style={accountColorStyle(account.color)}
			data-tone={account.tone}
			data-flx="konvo-agents.account-pill"
		>
			{account.label}
		</span>
	);
}

export function toolLogo(tool: AgentToolInfo['tool'], size = 14) {
	switch (tool) {
		case 't3':
			return <T3Mark size={size} />;
		default:
			return <T3Mark size={size} />;
	}
}

/** "T3 Code 0.0.45" with its mark, as a chip. */
export function ToolChip({tool, withVersion = true}: {tool: AgentToolInfo; withVersion?: boolean}) {
	return (
		<span className={styles.chip} data-flx="konvo-agents.tool-chip">
			{toolLogo(tool.tool)}
			{tool.name}
			{withVersion && tool.version ? ` ${tool.version}` : ''}
		</span>
	);
}

/** Static chips for the tools Agents works with (T3 Code now; Codex and Claude Code soon). */
export function SupportedToolChips({className}: {className?: string}) {
	return (
		<div className={clsx(styles.chips, className)}>
			<span className={styles.chip}>
				<T3Mark size={14} />
				T3 Code
			</span>
			<span className={styles.chip}>
				<span className={styles.openai}>
					<OpenAILogo size={14} />
				</span>
				Codex
			</span>
			<span className={styles.chip}>
				<ClaudeLogo size={14} />
				Claude Code
			</span>
		</div>
	);
}

export function MachineGlyph({platform, size = 20}: {platform: AgentMachineView['platform']; size?: number}) {
	return machineIcon(platform) === 'laptop' ? (
		<IconDeviceLaptop size={size} stroke={1.6} />
	) : (
		<IconServer size={size} stroke={1.6} />
	);
}

export function OnlineDot({online, className}: {online: boolean; className?: string}) {
	return <span className={clsx(styles.dot, online && styles.dotOn, className)} aria-hidden="true" />;
}
